<script setup lang="ts">
import {
  computed,
  nextTick,
  onMounted,
  onUnmounted,
  reactive,
  toRefs,
  useTemplateRef,
} from 'vue';
import dayjs from 'dayjs';
import BasicEmptyFeed from '@/components/common/basic-empty-feed.vue';
import PersonalTechnicalLiteratureLists from '@/components/personal-news/personal-technical-literature-list.vue';
import {
  AdpDocument,
  FeedType,
  ThemeFeedPatent,
  ThemeFeedResearchPaper,
} from '@/types';
import { syncAdpDocumentActions } from '@/utils/adpDocument';
import { formatDayOfWeek } from '@/utils/formatters';
import { STATES } from '@/utils/swr';
import { useEmitter } from '@/utils/vue';

type TechnicalLiteratureDocType = 'research_paper' | 'patent';
type TechnicalLiterature = ThemeFeedResearchPaper | ThemeFeedPatent;

type Props = {
  docType: TechnicalLiteratureDocType;
  feedType: FeedType;
  feedDate: string;
  domesticDocuments: {
    items: (ThemeFeedResearchPaper | ThemeFeedPatent)[];
    state: keyof typeof STATES | 'idle';
  };
  foreignDocuments?: {
    items: (ThemeFeedResearchPaper | ThemeFeedPatent)[];
    state: keyof typeof STATES | 'idle';
  };
  toggleKeywordFeedType?: () => void;
};
const props = withDefaults(defineProps<Props>(), {
  foreignDocuments: () => ({
    items: [] as (ThemeFeedResearchPaper | ThemeFeedPatent)[],
    state: 'idle',
  }),
});

const { domesticDocuments, foreignDocuments, feedDate } = toRefs(props);
const emitter = useEmitter();

const formattedFeedDate = formatDayOfWeek(feedDate?.value ?? '');
const feedDateForSummary = computed(() =>
  dayjs(props.feedDate).format('YYYY-MM-DD'),
);

const handleDocumentUpdate = (document: AdpDocument) => {
  const documents =
    document.lang === 'en' ? foreignDocuments : domesticDocuments;
  syncAdpDocumentActions(documents.value.items, document);
};

const removedDocumentIds = reactive({
  research_paper: [],
  patent: [],
} as { [K in TechnicalLiteratureDocType]: number[] });
const removeDocument = ({ id, doc_type }: TechnicalLiterature) => {
  removedDocumentIds[doc_type].push(id);
};
const restoreDocument = ({ id, doc_type }: TechnicalLiterature) => {
  const removed = [...removedDocumentIds[doc_type]];
  if (removed.includes(id)) {
    removedDocumentIds[doc_type] = removed.filter(a => a !== id);
  }
};

onMounted(async () => {
  emitter.on('article-updated', handleDocumentUpdate);
  emitter.on('article-viewed', handleDocumentUpdate);
  emitter.on('personal-feed-article-removed', removeDocument);
  emitter.on('personal-feed-article-restored', restoreDocument);
});

onUnmounted(() => {
  emitter.off('article-updated', handleDocumentUpdate);
  emitter.off('article-viewed', handleDocumentUpdate);
  emitter.off('personal-feed-article-removed', removeDocument);
  emitter.off('personal-feed-article-restored', restoreDocument);
});

const hasPastTechnicalLiterature = computed(
  () =>
    [...domesticDocuments.value.items, ...foreignDocuments.value.items].filter(
      p => !p.is_new,
    ).length > 0,
);

const dateOptions = computed(() => {
  const options = [
    {
      label: formattedFeedDate,
      value: feedDateForSummary.value,
    },
  ];
  if (hasPastTechnicalLiterature.value) {
    options.push({
      label: '過去　　', // 幅を合わせるためにスペースを入れている
      value: '過去',
    });
  }
  return options;
});

const newTechLitRef = useTemplateRef('newTechLitRef');
const oldTechLitRef = useTemplateRef('oldTechLitRef');

const selectDate = (value: string | undefined) => {
  if (value === undefined) return;

  const targetElement =
    value === feedDateForSummary.value
      ? newTechLitRef.value
      : oldTechLitRef.value;
  if (!targetElement) return;

  nextTick(() => {
    // NOTE: スクロール位置はscroll-margin-topで調整している
    targetElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
  });
};
</script>

<template>
  <div class="technical-literature-feed" :key="`${docType}-${feedType}`">
    <div class="description c-text-regular--m">
      {{
        docType === 'patent' ? '特許' : '論文'
      }}は毎週月曜日の4:00に更新されます。
    </div>
    <template v-if="feedType === 'foreign' && docType === 'patent'">
      <BasicEmptyFeed>海外特許の配信は行っておりません。</BasicEmptyFeed>
    </template>
    <template
      v-else-if="
        (['all', 'domestic'].includes(feedType) &&
          domesticDocuments.state === STATES.SUCCESS) ||
        (['all', 'foreign'].includes(feedType) &&
          foreignDocuments.state === STATES.SUCCESS)
      "
    >
      <div class="new-tech-lit" ref="newTechLitRef">
        <PersonalTechnicalLiteratureLists
          :doc-type="docType"
          :feed-type="feedType"
          :domestic-documents="
            domesticDocuments.items.filter(
              p => p.is_new && !removedDocumentIds[p.doc_type].includes(p.id),
            )
          "
          :foreign-documents="
            foreignDocuments.items.filter(
              p => p.is_new && !removedDocumentIds[p.doc_type].includes(p.id),
            )
          "
          :date-options
          :is-past="false"
          :feed-date="feedDateForSummary"
          @select-date="selectDate"
        />
      </div>
      <div
        v-if="hasPastTechnicalLiterature"
        class="old-tech-lit"
        ref="oldTechLitRef"
      >
        <PersonalTechnicalLiteratureLists
          :doc-type="docType"
          :feed-type="feedType"
          :domestic-documents="
            domesticDocuments.items.filter(
              p => !p.is_new && !removedDocumentIds[p.doc_type].includes(p.id),
            )
          "
          :foreign-documents="
            foreignDocuments.items.filter(
              p => !p.is_new && !removedDocumentIds[p.doc_type].includes(p.id),
            )
          "
          :date-options
          :is-past="true"
          :feed-date="feedDateForSummary"
          @select-date="selectDate"
        />
      </div>
    </template>
  </div>
</template>

<style lang="scss" scoped>
.technical-literature-feed {
  display: flex;
  flex-direction: column;
  width: 616px;
}

.description {
  color: $color-gray600;
  margin-bottom: 24px;
}

.old-tech-lit {
  margin-top: 40px;
}

.new-tech-lit,
.old-tech-lit {
  scroll-margin-top: calc(var(--layout-sticky-top-under-tabs, 112px) + 16px);
}
</style>
